import {
	DictionaryConsumer,
	DictionaryProvider,
	DictionaryExpression,
	DynamicDictionaryExpression,
} from "./Dictionary";
import { MutableDictionary } from "./MutableDictionary";
import { Scalar, ScalarProvider, ScalarConsumer, scalarValue } from "./Scalar";
import { MutableVariable } from "./Variable";

export class GroupBy<KeyType, ValueType, GroupType> extends DictionaryExpression<
	KeyType,
	ValueType,
	DictionaryProvider<KeyType, ValueType>,
	GroupType
> {
	private _entries = new Map<GroupType, MutableDictionary<KeyType, ValueType>>();
	private _inverse = new Map<KeyType, [GroupType, MutableDictionary<KeyType, ValueType>]>();
	private _subscriptions = new Map<KeyType, [ScalarProvider<GroupType>, ScalarConsumer<GroupType>]>();

	constructor(
		source: DictionaryProvider<KeyType, ValueType>,
		private readonly group_fn: ($: [KeyType, ValueType]) => Scalar<GroupType>,
		readonly __origin?: string
	) {
		super(source);
	}

	private addToGroup(group_value: GroupType, key: KeyType, value: ValueType) {
		if (this._inverse.has(key)) {
			const [prev_group_value] = this._inverse.get(key)!;
			if (prev_group_value === group_value) return;
			this.removeFromGroup(key);
		}
		let group = this._entries.get(group_value);
		if (group === undefined) {
			group = new MutableDictionary<KeyType, ValueType>();
			this._entries.set(group_value, group);
			this._inverse.set(key, [group_value, group]);
			group.add(key, value);
			this._subscriber!.add(group_value, group);
		} else {
			this._inverse.set(key, [group_value, group]);
			group.add(key, value);
		}
		this._size?.ensure(this._entries.size);
	}

	private removeFromGroup(key: KeyType) {
		const [group_value, group] = this._inverse.get(key)!;
		this._inverse.delete(key);
		group.remove(key);
		if (group.size.value === 0) {
			this._entries.delete(group_value);
			this._subscriber!.remove(group_value);
		}
		this._size?.ensure(this._entries.size);
	}

	add(key: KeyType, value: ValueType) {
		const group_value = this.group_fn([key, value]);
		if (group_value instanceof ScalarProvider) {
			const subscription = group_value.subscribe({
				update: (group_value: GroupType) => {
					this.addToGroup(group_value, key, value);
				},
			});
			this._subscriptions.set(key, [group_value, subscription]);
		} else {
			this.addToGroup(group_value, key, value);
		}
	}

	remove(key: KeyType) {
		if (this._subscriptions.has(key)) {
			const [group_value_scalar, subscription] = this._subscriptions.get(key)!;
			this._subscriptions.delete(key);
			group_value_scalar.unsubscribe(subscription);
		}
		this.removeFromGroup(key);
	}

	get entries(): ReadonlyMap<GroupType, DictionaryProvider<KeyType, ValueType>> {
		if (this._subscriber === undefined) {
			const groups = new Map<GroupType, MutableDictionary<KeyType, ValueType>>();
			for (const $ of this.source.entries) {
				const group_value = scalarValue(this.group_fn($));
				let group = groups.get(group_value);
				if (group === undefined) {
					group = new MutableDictionary<KeyType, ValueType>();
					groups.set(group_value, group);
				}
				group.add($[0], $[1]);
			}
			return groups;
		}
		return this._entries;
	}

	private _size: MutableVariable<number> | undefined;
	get size(): ScalarProvider<number> {
		return (this._size ??= new MutableVariable(this._entries.size));
	}

	unsubscribe(subscriber: DictionaryConsumer<GroupType, DictionaryProvider<KeyType, ValueType>>) {
		super.unsubscribe(subscriber);
		for (const [, [group_value_scalar, subscription]] of this._subscriptions) {
			group_value_scalar.unsubscribe(subscription);
		}
		this._subscriptions.clear();
		this._inverse.clear();
		this._entries.clear();
	}
}

export class DynamicGroupBy<KeyType, ValueType, GroupType> extends DynamicDictionaryExpression<
	KeyType,
	ValueType,
	GroupBy<KeyType, ValueType, GroupType>
> {
	constructor(
		source: ScalarProvider<DictionaryProvider<KeyType, ValueType>>,
		private readonly group_fn: ($: [KeyType, ValueType]) => Scalar<GroupType>,
		readonly __origin?: string
	) {
		super(source);
	}

	initialize() {
		return new GroupBy<KeyType, ValueType, GroupType>(this.source.value, this.group_fn);
	}
}
